/**
 * Yacht Owner Dashboard
 */

import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useNotifications } from '../../context/NotificationContext';
import { requestsAPI, yachtsAPI } from '../../services/api';
import {
  PlusCircle,
  Ship,
  Clock,
  CheckCircle,
  Edit2,
  X,
  Check,
  Camera,
} from 'lucide-react';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';
const UPLOADS_BASE_URL = API_BASE_URL.replace(/\/api\/?$/, '');
import DashboardProfileCard from '../../components/common/DashboardProfileCard';

function Dashboard() {
  const { user } = useAuth();
  const { showToast } = useNotifications();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    activeRequests: 0,
    completedServices: 0,
  });
  const [yacht, setYacht] = useState(null);
  const [showYachtForm, setShowYachtForm] = useState(false);
  const [yachtFormLoading, setYachtFormLoading] = useState(false);
  const [yachtFormData, setYachtFormData] = useState({
    name: '',
    yacht_type: 'motor',
    length: '',
  });
  const [photos, setPhotos] = useState([]);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [deletingPhoto, setDeletingPhoto] = useState(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      const [requestsData, yachtsData] = await Promise.all([
        requestsAPI.getMyRequests(),
        yachtsAPI.getMyYachts(),
      ]);

      // API returns { success: true, data: [...] }
      const requests = requestsData.data || requestsData.requests || [];
      const activeRequests = requests.filter(
        r => ['pending', 'confirmed', 'in_progress', 'active', 'matched'].includes(r.status)
      );
      const completedRequests = requests.filter(r => r.status === 'completed');

      setStats({
        activeRequests: activeRequests.length,
        completedServices: completedRequests.length,
      });

      // Get the single yacht
      const yachtsList = yachtsData.data || yachtsData.yachts || [];
      const userYacht = yachtsList[0] || null;
      setYacht(userYacht);
      if (userYacht) {
        setYachtFormData({
          name: userYacht.name || '',
          yacht_type: userYacht.yacht_type || 'motor',
          length: userYacht.length_m?.toString() || userYacht.length?.toString() || '',
        });
        setPhotos(userYacht.photos || []);
      }
    } catch (error) {
      console.error('Failed to load dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleYachtFormChange = (e) => {
    const { name, value } = e.target;
    setYachtFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleYachtSubmit = async (e) => {
    e.preventDefault();
    setYachtFormLoading(true);

    try {
      const yachtData = {
        name: yachtFormData.name,
        yacht_type: yachtFormData.yacht_type,
        length_m: parseFloat(yachtFormData.length),
      };

      await yachtsAPI.updateYacht(yacht.id, yachtData);
      showToast('Yacht updated successfully', 'success');
      setShowYachtForm(false);
      loadDashboardData();
    } catch (error) {
      showToast(error.message || 'Failed to save yacht', 'error');
    } finally {
      setYachtFormLoading(false);
    }
  };

  const closeYachtForm = () => {
    setShowYachtForm(false);
    if (yacht) {
      setYachtFormData({
        name: yacht.name || '',
        yacht_type: yacht.yacht_type || 'motor',
        length: yacht.length_m?.toString() || yacht.length?.toString() || '',
      });
      setPhotos(yacht.photos || []);
    }
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      showToast('Please select an image file', 'error');
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      showToast('Image must be less than 10MB', 'error');
      return;
    }

    setUploadingPhoto(true);
    try {
      const response = await yachtsAPI.uploadPhoto(yacht.id, file);
      setPhotos(response.data?.photos || [...photos, response.data?.url]);
      showToast('Photo uploaded successfully', 'success');
    } catch (error) {
      showToast(error.message || 'Failed to upload photo', 'error');
    } finally {
      setUploadingPhoto(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleDeletePhoto = async (photoUrl) => {
    if (!window.confirm('Remove this photo?')) return;

    setDeletingPhoto(photoUrl);
    try {
      const response = await yachtsAPI.deletePhoto(yacht.id, photoUrl);
      setPhotos(response.data?.photos || photos.filter(p => p !== photoUrl));
      showToast('Photo removed', 'success');
    } catch (error) {
      showToast(error.message || 'Failed to remove photo', 'error');
    } finally {
      setDeletingPhoto(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner w-8 h-8" />
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Welcome Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-display font-bold text-navy-900">
            Welcome back, {user?.first_name}!
          </h1>
          <p className="text-navy-600 mt-1">
            Manage your mooring requests
          </p>
        </div>
        <Link
          to="/owner/request/new"
          className="mt-4 sm:mt-0 btn-primary inline-flex items-center"
        >
          <PlusCircle className="h-5 w-5 mr-2" />
          New Request
        </Link>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
        <div className="card">
          <div className="flex items-center">
            <div className="h-12 w-12 bg-primary-100 rounded-lg flex items-center justify-center">
              <Clock className="h-6 w-6 text-primary-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm text-navy-500">Active Requests</p>
              <p className="text-2xl font-bold text-navy-900">{stats.activeRequests}</p>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="flex items-center">
            <div className="h-12 w-12 bg-green-100 rounded-lg flex items-center justify-center">
              <CheckCircle className="h-6 w-6 text-green-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm text-navy-500">Completed</p>
              <p className="text-2xl font-bold text-navy-900">{stats.completedServices}</p>
            </div>
          </div>
        </div>
      </div>

      {/* My Yacht Card */}
      {yacht && (
        <div className="card">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <div className="h-14 w-14 bg-gold-100 rounded-xl flex items-center justify-center">
                <Ship className="h-7 w-7 text-gold-600" />
              </div>
              <div className="ml-4">
                <h2 className="text-lg font-semibold text-navy-900">{yacht.name}</h2>
                <p className="text-navy-500">
                  {yacht.length_m || yacht.length}m • {yacht.yacht_type} yacht
                </p>
              </div>
            </div>
            <button
              onClick={() => setShowYachtForm(true)}
              className="btn-secondary text-sm"
            >
              <Edit2 className="h-4 w-4 mr-1" />
              Edit
            </button>
          </div>
        </div>
      )}

      {/* My Profile (Read-Only) */}
      <DashboardProfileCard />

      {/* Edit Yacht Modal */}
      {showYachtForm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h2 className="text-xl font-display font-semibold text-navy-900">
                Edit Yacht
              </h2>
              <button
                onClick={closeYachtForm}
                className="p-2 text-navy-400 hover:text-navy-600 hover:bg-gray-100 rounded-lg"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <form onSubmit={handleYachtSubmit} className="p-6 space-y-5">
              <div>
                <label className="block text-sm font-medium text-navy-700 mb-2">
                  Yacht Name
                </label>
                <input
                  type="text"
                  name="name"
                  value={yachtFormData.name}
                  onChange={handleYachtFormChange}
                  className="input-field"
                  placeholder="e.g., Ocean Dream"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-navy-700 mb-2">
                  Yacht Type
                </label>
                <select
                  name="yacht_type"
                  value={yachtFormData.yacht_type}
                  onChange={handleYachtFormChange}
                  className="input-field"
                >
                  <option value="motor">Motor</option>
                  <option value="sailing">Sailing</option>
                  <option value="catamaran">Catamaran</option>
                  <option value="superyacht">Superyacht</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-navy-700 mb-2">
                  Length (meters)
                </label>
                <input
                  type="number"
                  name="length"
                  value={yachtFormData.length}
                  onChange={handleYachtFormChange}
                  className="input-field"
                  placeholder="12"
                  step="0.1"
                  min="1"
                  required
                />
              </div>

              {/* Photo Upload Section */}
              <div>
                <label className="block text-sm font-medium text-navy-700 mb-2">
                  Photos
                </label>

                {/* Existing Photos */}
                {photos.length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-3">
                    {photos.map((photo, idx) => (
                      <div key={idx} className="relative group">
                        <div className="h-20 w-20 rounded-lg overflow-hidden">
                          <img
                            src={photo.startsWith('http') ? photo : `${UPLOADS_BASE_URL}${photo}`}
                            alt={`Yacht ${idx + 1}`}
                            className="h-full w-full object-cover"
                          />
                        </div>
                        <button
                          type="button"
                          onClick={() => handleDeletePhoto(photo)}
                          disabled={deletingPhoto === photo}
                          className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600"
                        >
                          {deletingPhoto === photo ? (
                            <span className="spinner h-3 w-3" />
                          ) : (
                            <X className="h-3 w-3" />
                          )}
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                {/* Upload Button */}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handlePhotoUpload}
                  className="hidden"
                  id="yacht-photo-input"
                />
                <label
                  htmlFor="yacht-photo-input"
                  className={`inline-flex items-center px-4 py-2 border border-dashed border-navy-300 rounded-lg text-sm text-navy-600 hover:border-primary-500 hover:text-primary-600 cursor-pointer transition-colors ${uploadingPhoto ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  {uploadingPhoto ? (
                    <>
                      <span className="spinner h-4 w-4 mr-2" />
                      Uploading...
                    </>
                  ) : (
                    <>
                      <Camera className="h-4 w-4 mr-2" />
                      Add Photo
                    </>
                  )}
                </label>
                <p className="text-xs text-navy-400 mt-2">Max 10MB per photo. JPG, PNG supported.</p>
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={closeYachtForm}
                  className="btn-secondary"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={yachtFormLoading}
                  className="btn-primary"
                >
                  {yachtFormLoading ? (
                    <span className="flex items-center">
                      <span className="spinner h-5 w-5 mr-2" />
                      Saving...
                    </span>
                  ) : (
                    <>
                      <Check className="h-5 w-5 mr-2" />
                      Save Changes
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default Dashboard;
